import React, { useState, useEffect, createContext } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { getUserProfilePic } from '../data/Graph';
import { getApplications, getTools, getJobTitle, getProjects } from '../data/SQL';
import { isMobile, isTablet } from 'react-device-detect'; // Import isMobile and isTablet from react-device-detect
import { userToolsCache } from '../services/userToolsCache';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { accounts, instance } = useMsal();
  const [userName, setUserName] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userPic, setUserPic] = useState(null);
  const [userTitle, setUserTitle] = useState(null);
  const [userProjects, setUserProjects] = useState(null);
  const [userApplications, setUserApplications] = useState(null);
  const [userTools, setUserTools] = useState(null);
  const [deviceType, setDeviceType] = useState('desktop'); // Default to desktop
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Detect device type on component mount
  useEffect(() => {
    if (isMobile) { setDeviceType('mobile'); }
    else if (isTablet) { setDeviceType('tablet'); }
    else { setDeviceType('desktop'); }
  }, []);

  // Get user account
  useEffect(() => {
    const getAccount = async () => {
      try {
        const activeAccount = accounts[0];
        
        // If no account, cleanup cache polling
        if (!activeAccount) {
          userToolsCache.cleanup();
          setLoading(false);
          return;
        }
        
        setTimeout(async () => {
          if (activeAccount) {
            try {
              const response = await instance.acquireTokenSilent({
                account: activeAccount,
                scopes: ["openid", "profile", "User.Read", "Mail.Send"],
              });
              fetchAndSetUserDetails(response.accessToken, response.account.name, response.account.username);
            } catch (error) {
              // If interaction is required, fallback to redirect
              if (error.name === "InteractionRequiredAuthError") {
                try {
                  await instance.acquireTokenRedirect({
                    account: activeAccount,
                    scopes: ["openid", "profile", "User.Read", "Mail.Send"],
                  });
                } catch (redirectError) {
                  console.error('Redirect error during token acquisition:', redirectError);
                  userToolsCache.cleanup();
                  setLoading(false);
                }
              } else {
                console.error('Error fetching user details:', error);
                userToolsCache.cleanup();
                setLoading(false);
              }
            }
          } else {
            userToolsCache.cleanup();
            setLoading(false);
          }
        }, 0);
      } catch (error) {
        console.error('Error in getAccount:', error);
        userToolsCache.cleanup();
        setLoading(false);
      }
    };
    getAccount();
  }, [accounts, instance]);

  const fetchAndSetUserDetails = async (accessToken, name, email) => {
    email = email.toLowerCase();
    setUserName(name);
    setUserEmail(email);
    setAccessToken(accessToken);

    try {
      // Get cached tools immediately if available
      let cachedTools = null;
      try {
        cachedTools = await userToolsCache.getUserTools(email);
        if (cachedTools !== null) {
          setUserTools(cachedTools);
        }
      } catch (cacheError) {
        console.warn('Cache lookup failed, will fetch fresh:', cacheError);
      }

      // Fetch other user details in parallel
      const otherDetailsPromises = [
        getUserProfilePic(accessToken).then(setUserPic),
        getJobTitle(email).then(setUserTitle),
        getProjects(email).then(setUserProjects),
        getApplications(email).then(setUserApplications)
      ];

      // If we don't have cached tools, add tools to the parallel fetch
      if (cachedTools === null) {
        otherDetailsPromises.push(getTools(email).then(setUserTools));
      }

      await Promise.all(otherDetailsPromises);

      // Subscribe to cache updates for tools
      userToolsCache.subscribe((updatedTools) => {
        setUserTools(updatedTools);
      });

      // Note: We keep the subscription active for the session
      // Cleanup happens automatically in userToolsCache.cleanup()

    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh user tools manually
  const refreshUserTools = async () => {
    if (!userEmail) return;
    
    try {
      const freshTools = await userToolsCache.forceRefresh(userEmail);
      setUserTools(freshTools);
      return freshTools;
    } catch (error) {
      console.error('Failed to refresh user tools:', error);
      throw error;
    }
  };

  const loginContextValue = {
    userName,
    userEmail,
    userPic,
    userTitle,
    userProjects,
    userApplications,
    userTools,
    deviceType,
    accessToken,
    loading,
    refreshUserTools
  };

  return (
    <AuthContext.Provider value={loginContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export function AuthenticatedRoute() {
  const { accounts } = useMsal();
  const location = useLocation();
  const isAuthenticated = accounts.length > 0;

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem(
        'postLoginRedirect',
        location.pathname + location.search
      );
    }
  }, [isAuthenticated, location]);

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }
  return <Outlet />;
}

export function UnauthenticatedRoute() {
  const { accounts } = useMsal();
  const location = useLocation();
  const isAuthenticated = accounts.length > 0;

  if (isAuthenticated) {
    if (location.pathname === '/private' || location.pathname === '/private/') {
      return <Navigate to="/private/home" replace />;
    }
    return <Outlet />;
  }

  return <Outlet />;
}
